import { useEffect, useRef } from "react";

/**
 * Custom hook to keep the screen awake using the Screen Wake Lock API
 * Automatically releases the lock when the component unmounts or when disabled
 */
export function useWakeLock(enabled: boolean = true) {
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!("wakeLock" in navigator)) {
      console.warn("Screen Wake Lock API is not supported in this browser");
      return;
    }

    if (!enabled) {
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
      return;
    }

    let isMounted = true;

    const requestWakeLock = async () => {
      try {
        const wakeLock = await navigator.wakeLock.request("screen");
        if (isMounted) {
          wakeLockRef.current = wakeLock;
        } else {
          wakeLock.release().catch(() => {});
        }
      } catch (err) {
        console.warn("Failed to acquire wake lock:", err);
      }
    };

    requestWakeLock();

    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible" && isMounted && enabled) {
        if (!wakeLockRef.current) {
          await requestWakeLock();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup function
    return () => {
      isMounted = false;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, [enabled]);

  return wakeLockRef.current;
}
